import { useCallback, useEffect, useState } from "react";
const SEEN_ROUTES = { "/rac/ota": "rac_ota", "/vehicles": "rac_transport", "/rac/psychotest": "rac_psy", "/rac/meals": "rac_meals" };
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Ticket, CheckSquare, Boxes, Car, Building2, Wrench, FileText, Database, Users, ScrollText, Bell, Sun, Moon, LogOut, Menu, Plus, BookOpen, ClipboardList, GraduationCap, BarChart3, ShoppingCart, BrainCircuit, Plane, UtensilsCrossed, History, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useFetch } from "@/hooks/useFetch";
import { api } from "@/lib/api";
import { ROLE_LABELS, roleAllowed } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { NewRequestDialog } from "@/components/tickets/NewRequestDialog";
import { AiAssistant } from "@/components/common/AiAssistant";
import { NextStepPrompt } from "@/components/common/NextStepPrompt";

const NAV_GROUPS = [
  { title: "Utama — Untuk Semua", items: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/tickets", label: "Pipeline Service", icon: Ticket, badge: "tickets" },
  ] },
  { title: "General Affair", items: [
    { to: "/reports", label: "Laporan Performa GA", icon: BarChart3, roles: ["supervisor", "super_admin", "ga_staff", "asset_pic", "legal_admin"] },
    { to: "/maintenance", label: "Maintenance", icon: Wrench, badge: "maintenance", end: true },
    { to: "/maintenance/history", label: "History Maintenance", icon: History, sub: true },
    { to: "/departures", label: "Pemberangkatan Siswa", icon: GraduationCap, badge: "departures" },
    { to: "/purchase", label: "Purchase", icon: ShoppingCart, badge: "purchase", roles: ["supervisor", "super_admin", "ga_staff", "asset_pic", "accounting", "manager", "coo", "ceo"] },
    { to: "/accounting", label: "Dashboard Accounting", icon: Calculator, roles: ["supervisor", "super_admin", "ga_staff", "asset_pic", "accounting", "manager", "coo", "ceo"] },
    { to: "/assignments", label: "Penugasan", icon: ClipboardList, badge: "assignments", roles: ["supervisor", "super_admin", "ga_staff", "asset_pic", "legal_admin"] },
    { to: "/approvals", label: "Approval Inbox", icon: CheckSquare, badge: "approvals", roles: ["supervisor", "manager", "coo", "ceo", "super_admin", "legal_admin"] },
  ] },
  { title: "Relasi Andalan Colaborindo (RAC)", items: [
    { to: "/rac/ota", label: "Online Travel Agency (OTA)", icon: Plane, badge: "rac_ota" },
    { to: "/vehicles", label: "Vehicles - Transport", icon: Car, badge: "rac_transport" },
    { to: "/rac/psychotest", label: "Psikotest Service", icon: BrainCircuit, badge: "rac_psy" },
    { to: "/rac/meals", label: "Meals Service", icon: UtensilsCrossed, badge: "rac_meals" },
  ] },
  { title: "Administrasi", items: [
    { to: "/master", label: "Master Data", icon: Database, roles: ["super_admin", "ga_staff", "asset_pic", "legal_admin", "rac_staff"] },
    { to: "/facilities", label: "Facility List up", icon: Building2 },
    { to: "/assets", label: "Assets List up", icon: Boxes },
    { to: "/documents", label: "Legal Documents", icon: FileText, badge: "documents" },
    { to: "/audit", label: "Audit Trail", icon: ScrollText, roles: ["super_admin", "supervisor", "legal_admin"] },
    { to: "/users", label: "Users & Roles", icon: Users, roles: ["super_admin"] },
  ] },
  { title: "Bantuan", items: [
    { to: "/guide", label: "Panduan & SOP", icon: BookOpen },
  ] },
];

function SideNav({ onNavigate, badges, onSeen }) {
  const { user } = useAuth();
  return (
    <div className="flex h-full flex-col bg-[hsl(var(--sidebar))] text-slate-200">
      <div className="flex items-center gap-2 px-5 py-5">
        <div className="flex h-8 w-8 items-center justify-center rounded bg-primary font-extrabold text-white">Y</div>
        <div><div className="font-extrabold leading-none tracking-tight text-white">Yutaka Digital Request</div><div className="text-[10px] uppercase tracking-widest text-slate-400">Yutaka Group</div></div>
      </div>
      <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 pb-4" data-testid="sidebar-nav">
        {NAV_GROUPS.map((g) => {
          const items = g.items.filter((n) => !n.roles || roleAllowed(user.role, n.roles));
          if (!items.length) return null;
          const hot = items.some((n) => n.badge && badges?.[n.badge] > 0);
          return (
            <div key={g.title} className="mb-5" data-testid={`nav-group-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className={cn("mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-widest", hot ? "text-pink-400" : "text-slate-500")}>{g.title}</div>
              <div className="space-y-0.5">
                {items.map((n) => {
                  const count = n.badge ? badges?.[n.badge] || 0 : 0;
                  const fresh = n.badge ? badges?.[`${n.badge}_new`] || 0 : 0;
                  return (
                  <NavLink key={n.to} to={n.to} end={n.end} onClick={() => { onNavigate?.(); if (fresh > 0) onSeen?.(n.badge); }} data-testid={`nav-${n.to.slice(1).replace(/\//g, "-")}`}
                    className={({ isActive }) => cn("flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors", n.sub && "ml-4 border-l border-white/10 py-1.5 pl-3 text-xs", isActive ? "bg-primary/15 text-white border-l-2 border-primary" : "text-slate-400 hover:bg-white/5 hover:text-white",
                      count > 0 && !isActive && (fresh > 0 ? "bg-red-500/20 text-red-100 hover:bg-red-500/30" : "bg-pink-500/20 text-pink-200 hover:bg-pink-500/30"))}>
                    <n.icon className={cn("h-4 w-4", count > 0 && (fresh > 0 ? "text-red-400" : "text-pink-400"))} /> <span className="flex-1">{n.label}</span>
                    {count > 0 && <span className={cn("flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-bold text-white", fresh > 0 ? "bg-red-500 animate-pulse" : "bg-pink-500")} title={fresh > 0 ? `${fresh} pesanan baru sejak terakhir dibuka` : `${count} belum close`} data-testid={`nav-badge-${n.badge}`} data-fresh={fresh > 0 ? "true" : "false"}>{count}</span>}
                  </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="px-5 py-4 text-[10px] text-slate-500">Yutaka Digital Request · V1</div>
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [newReq, setNewReq] = useState(false);
  const [reqPreset, setReqPreset] = useState(null);
  useEffect(() => {
    const h = (e) => { setReqPreset(e.detail?.service_type || null); setNewReq(true); };
    window.addEventListener("ydr:new-request", h);
    return () => window.removeEventListener("ydr:new-request", h);
  }, []);
  const { data: notif, refetch } = useFetch("/notifications");
  const unread = notif?.unread || 0;
  const { data: badges, refetch: refetchBadges } = useFetch("/nav-badges");
  const loc = useLocation();
  useEffect(() => { refetchBadges(); refetch(); const id = setInterval(() => { refetchBadges(); refetch(); }, 60000); return () => clearInterval(id); }, [loc.pathname, refetchBadges, refetch]);
  const markSeen = useCallback(async (key) => { try { await api.post(`/rac/nav-seen/${key}`); refetchBadges(); } catch { /* abaikan */ } }, [refetchBadges]);
  useEffect(() => {
    const key = Object.entries(SEEN_ROUTES).find(([path]) => loc.pathname.startsWith(path))?.[1];
    if (key && badges?.[`${key}_new`] > 0) markSeen(key);
  }, [loc.pathname, badges, markSeen]);

  return (
    <div className="min-h-screen grain">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 lg:block"><SideNav badges={badges} onSeen={markSeen} /></aside>
      <Sheet open={open} onOpenChange={setOpen}><SheetContent side="left" className="w-64 p-0"><SideNav onNavigate={() => setOpen(false)} badges={badges} onSeen={markSeen} /></SheetContent></Sheet>
      <div className="lg:pl-60">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b bg-background/80 px-4 backdrop-blur-md sm:px-6" data-testid="app-header">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
          <div className="ml-auto flex items-center gap-1 sm:gap-2">
            <Button size="sm" onClick={() => setNewReq(true)} data-testid="header-new-request-btn"><Plus className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Service Request</span></Button>
            <Button variant="ghost" size="icon" onClick={() => nav("/notifications")} className="relative" data-testid="notification-bell-btn">
              <Bell className="h-5 w-5" />
              {unread > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white" data-testid="notification-unread-count">{unread}</span>}
            </Button>
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle-button">{theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}</Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-accent" data-testid="user-menu-btn">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-xs font-bold">{user.name?.[0]?.toUpperCase()}</div>
                  <div className="hidden text-left sm:block"><div className="text-xs font-semibold leading-none">{user.name}</div><div className="text-[10px] text-muted-foreground" data-testid="user-role-badge">{ROLE_LABELS[user.role]}</div></div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="text-xs">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn"><LogOut className="mr-2 h-4 w-4" /> Keluar</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="relative z-10 p-4 sm:p-6 lg:p-8"><Outlet context={{ refetchNotif: refetch }} /></main>
        <AiAssistant />
        <NextStepPrompt />
      </div>
      <NewRequestDialog open={newReq} onOpenChange={(o) => { setNewReq(o); if (!o) setReqPreset(null); }} preset={reqPreset} onCreated={(t) => nav(`/tickets/${t.id}`)} />
    </div>
  );
}
